import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Camera, LoaderCircle, QrCode, RefreshCcw } from 'lucide-react';
import ArTrackingScene from '../../components/ar/ArTrackingScene';
import { getTrackingModeInstructions, resolveArExperience } from '../../features/ar/arConfig';
import { getArStartErrorMessage, isArSupported } from '../../features/ar/experienceGuards';
import { logArEvent } from '../../features/ar/logger';
import { loadArRuntime } from '../../features/ar/scriptLoader';
import type { ArDebugSnapshot, ArModelState, ArTrackingMode, ArTrackingState, ResolvedArExperience } from '../../features/ar/types';
import { api } from '../../lib/api';
import type { Product } from '../../types';

const DETECTION_TIMEOUT_MS = 9000;
const DEFAULT_AR_MODE: ArTrackingMode = 'marker-tracking';

const flattenProducts = (categories: Array<{ products?: Product[] }> = []) =>
    categories.flatMap((category) => category.products || []);

const trackingStatusLabel: Record<ArTrackingState, string> = {
    idle: 'Pronto para iniciar',
    starting: 'Preparando a camera',
    scanning: 'Buscando alvo',
    'target-found': 'Alvo detectado',
    'target-lost': 'Alvo perdido',
    'camera-denied': 'Permissao de camera negada',
    unsupported: 'Dispositivo incompativel',
    'runtime-error': 'Erro na sessao AR',
};

const modelStatusLabel: Record<ArModelState, string> = {
    idle: 'Modelo aguardando',
    loading: 'Carregando modelo 3D',
    loaded: 'Modelo 3D pronto',
    error: 'Falha no modelo 3D',
};

const StoreArPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const debugEnabled = searchParams.get('debug') === '1';
    const autoStart = searchParams.get('autostart') === '1';
    const [mode] = useState<ArTrackingMode>((searchParams.get('mode') as ArTrackingMode) || DEFAULT_AR_MODE);
    const [experience, setExperience] = useState<ResolvedArExperience | null>(null);
    const [loadError, setLoadError] = useState('');
    const [bootError, setBootError] = useState('');
    const [started, setStarted] = useState(false);
    const [sceneKey, setSceneKey] = useState(0);
    const [trackingState, setTrackingState] = useState<ArTrackingState>('idle');
    const [modelState, setModelState] = useState<ArModelState>('idle');
    const [targetVisible, setTargetVisible] = useState(false);
    const [detectionTimeoutHit, setDetectionTimeoutHit] = useState(false);
    const [cameraFeedVisible, setCameraFeedVisible] = useState(false);
    const startLockRef = useRef(false);
    const detectionTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        if (!slug) {
            return;
        }

        api.get(`/establishments/${slug}`)
            .then(({ data }) => {
                const nextProducts = flattenProducts(data.categories || []);
                setExperience(resolveArExperience({
                    slug,
                    mode,
                    products: nextProducts,
                    productId: searchParams.get('item') || undefined,
                    configKey: searchParams.get('config') || undefined,
                }));
                setLoadError('');
            })
            .catch((error) => {
                console.error('Failed to load store AR data', error);
                setLoadError('Nao foi possivel preparar a experiencia AR desta loja.');
            });
    }, [mode, searchParams, slug]);

    useEffect(() => {
        if (trackingState !== 'scanning' && trackingState !== 'starting') {
            if (detectionTimeoutRef.current) {
                window.clearTimeout(detectionTimeoutRef.current);
                detectionTimeoutRef.current = null;
            }
            return;
        }

        detectionTimeoutRef.current = window.setTimeout(() => {
            setDetectionTimeoutHit(true);
            logArEvent('warn', 'detection_timeout', { mode, timeoutMs: DETECTION_TIMEOUT_MS });
        }, DETECTION_TIMEOUT_MS);

        return () => {
            if (detectionTimeoutRef.current) {
                window.clearTimeout(detectionTimeoutRef.current);
                detectionTimeoutRef.current = null;
            }
        };
    }, [mode, trackingState]);

    useEffect(() => () => {
        if (detectionTimeoutRef.current) {
            window.clearTimeout(detectionTimeoutRef.current);
        }
    }, []);

    const resetUiState = () => {
        setTrackingState('idle');
        setModelState('idle');
        setBootError('');
        setTargetVisible(false);
        setDetectionTimeoutHit(false);
        setCameraFeedVisible(false);
    };

    const startAr = useCallback(async () => {
        if (!experience || startLockRef.current) {
            return;
        }

        if (!isArSupported()) {
            setTrackingState('unsupported');
            setBootError('Este navegador precisa de HTTPS e acesso a camera para abrir a experiencia AR.');
            logArEvent('warn', 'start_blocked_unsupported');
            return;
        }

        startLockRef.current = true;
        setDetectionTimeoutHit(false);
        setBootError('');
        setTrackingState('starting');
        setModelState('idle');
        logArEvent('info', 'ar_start_requested', { mode: experience.mode, configKey: experience.configKey, itemId: experience.itemId });

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: { facingMode: { ideal: 'environment' } },
            });
            stream.getTracks().forEach((track) => track.stop());
            logArEvent('info', 'camera_permission_granted', { mode: experience.mode });
            await loadArRuntime(experience.mode);
            setStarted(true);
            setSceneKey((current) => current + 1);
        } catch (error) {
            console.error('Failed to start AR', error);
            setStarted(false);
            if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
                setTrackingState('camera-denied');
                setBootError(getArStartErrorMessage(error));
                logArEvent('warn', 'camera_permission_denied', { mode: experience.mode });
                startLockRef.current = false;
                return;
            }

            setTrackingState('runtime-error');
            setBootError(getArStartErrorMessage(error));
            logArEvent('error', 'ar_start_failed', {
                mode: experience.mode,
                message: error instanceof Error ? error.message : 'unknown',
            });
        }

        startLockRef.current = false;
    }, [experience]);

    useEffect(() => {
        if (!autoStart || !experience || started || startLockRef.current || loadError) {
            return;
        }

        void startAr();
    }, [autoStart, experience, loadError, startAr, started]);

    const stopArSession = () => {
        setStarted(false);
        startLockRef.current = false;
        logArEvent('info', 'session_stop_requested', { mode, configKey: experience?.configKey });
    };

    const restartSession = () => {
        stopArSession();
        resetUiState();
        window.setTimeout(() => {
            setSceneKey((current) => current + 1);
            setStarted(true);
        }, 50);
    };

    if (!slug) {
        return null;
    }

    const activeMode = experience?.mode || mode;
    const instructions = getTrackingModeInstructions(activeMode);
    const showScanningOverlay = started && (trackingState === 'scanning' || trackingState === 'target-lost' || trackingState === 'starting');
    const showFoundOverlay = trackingState === 'target-found';
    const isErrorState = trackingState === 'camera-denied' || trackingState === 'unsupported' || trackingState === 'runtime-error' || modelState === 'error';
    const debugSnapshot: ArDebugSnapshot | null = experience ? {
        configKey: experience.configKey,
        itemName: experience.itemName,
        itemSlug: experience.runtimeConfig.binding.itemSlug,
        trackingMode: experience.mode,
        trackingState,
        modelState,
        targetName: experience.targetName,
        targetVisible,
        modelUrl: experience.modelUrl,
        transform: experience.transform,
    } : null;

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#030303] text-white">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(249,115,22,0.22),transparent_35%),radial-gradient(circle_at_bottom,rgba(255,255,255,0.08),transparent_30%)]" />

            <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-4 sm:px-6">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-[0.28em] text-primary/80">WebAR Gastroflow</p>
                        <h1 className="mt-2 text-2xl font-black text-white">{experience?.itemName || 'Experiencia AR'}</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to={`/store/${slug}`} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/85 transition hover:bg-white/10">
                            Voltar para a loja
                        </Link>
                        <button type="button" onClick={restartSession} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/85 transition hover:bg-white/10">
                            <RefreshCcw className="h-4 w-4" />
                            Reiniciar AR
                        </button>
                    </div>
                </div>

                <div className="grid flex-1 gap-4 lg:grid-cols-[1.15fr_0.85fr]">
                    <div className="relative min-h-[60dvh] overflow-hidden rounded-[2rem] border border-white/10 bg-black shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
                        {started && experience ? (
                            <ArTrackingScene
                                mode={experience.mode}
                                experience={experience}
                                sceneKey={sceneKey}
                                onScanning={() => setTrackingState('scanning')}
                                onTargetFound={() => {
                                    setTrackingState('target-found');
                                    setTargetVisible(true);
                                    setDetectionTimeoutHit(false);
                                }}
                                onTargetLost={() => {
                                    setTrackingState('target-lost');
                                    setTargetVisible(false);
                                }}
                                onModelLoading={() => setModelState('loading')}
                                onModelLoaded={() => setModelState('loaded')}
                                onModelError={() => setModelState('error')}
                                onRuntimeError={() => setTrackingState('runtime-error')}
                                onSessionDestroyed={() => {
                                    startLockRef.current = false;
                                    setTargetVisible(false);
                                    setCameraFeedVisible(false);
                                }}
                                onCameraFeedReady={() => setCameraFeedVisible(true)}
                            />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_center,rgba(249,115,22,0.18),transparent_40%),linear-gradient(180deg,#050505_0%,#090909_100%)] p-8 text-center">
                                <div className="max-w-sm space-y-4">
                                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-primary/30 bg-primary/15 text-primary">
                                        <Camera className="h-8 w-8" />
                                    </div>
                                    <div className="space-y-2">
                                        <h2 className="text-2xl font-black">Aponte a camera para a imagem/marcador</h2>
                                        <p className="text-sm text-white/70">
                                            {experience?.instruction || 'Carregando configuracao da experiencia AR.'}
                                        </p>
                                    </div>
                                    {!autoStart && (
                                        <button
                                            type="button"
                                            onClick={startAr}
                                            disabled={!experience || !!loadError}
                                            className="inline-flex min-w-[11rem] items-center justify-center rounded-full bg-primary px-5 py-3 text-sm font-black text-black transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            Ver AR
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {showScanningOverlay && (
                            <div className="pointer-events-none absolute inset-x-4 top-4 rounded-2xl border border-white/10 bg-black/55 px-4 py-3 backdrop-blur">
                                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/85">Scanning</p>
                                <p className="mt-2 text-sm text-white/85">
                                    {activeMode === 'image-tracking'
                                        ? 'Aponte a camera para a imagem-alvo impressa para ancorar o objeto 3D.'
                                        : 'Centralize o marcador impresso no enquadramento para travar o objeto 3D.'}
                                </p>
                            </div>
                        )}

                        {showFoundOverlay && (
                            <div className="pointer-events-none absolute inset-x-4 top-4 rounded-2xl border border-emerald-400/30 bg-emerald-500/15 px-4 py-3 backdrop-blur">
                                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-200">
                                    {activeMode === 'image-tracking' ? 'Imagem encontrada' : 'Marcador encontrado'}
                                </p>
                                <p className="mt-2 text-sm text-emerald-50">O objeto 3D esta ancorado ao alvo em tempo real.</p>
                            </div>
                        )}

                        {(modelState === 'loading' || modelState === 'error') && (
                            <div className="pointer-events-none absolute inset-x-4 bottom-4 rounded-2xl border border-white/10 bg-black/55 px-4 py-3 backdrop-blur">
                                <div className="flex items-center gap-3">
                                    {modelState === 'loading' ? (
                                        <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
                                    ) : (
                                        <AlertTriangle className="h-4 w-4 text-amber-300" />
                                    )}
                                    <span className="text-sm text-white/85">
                                        {modelState === 'loading' ? 'Carregando modelo 3D...' : 'Falha ao carregar o modelo 3D.'}
                                    </span>
                                </div>
                            </div>
                        )}

                        {detectionTimeoutHit && (
                            <div className="absolute inset-x-4 bottom-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 backdrop-blur">
                                Nenhum alvo foi detectado ainda. Confira iluminacao, foco e se o target impresso corresponde ao modo selecionado.
                            </div>
                        )}

                        {started && !cameraFeedVisible && (
                            <div className="absolute inset-x-4 bottom-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100 backdrop-blur">
                                A camera foi liberada, mas o video ainda nao apareceu. No iPhone, tente abrir no Safari completo e toque em Reiniciar AR.
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-4">
                        <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-5 shadow-[0_20px_50px_rgba(0,0,0,0.28)] backdrop-blur">
                            <p className="text-xs uppercase tracking-[0.24em] text-white/45">Experiencia AR</p>
                            <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white/72">
                                <p className="font-semibold text-white">Modo configurado</p>
                                <p className="mt-1">{activeMode === 'image-tracking' ? 'Image tracking' : 'Marker tracking'}</p>
                            </div>

                            <div className="mt-5 space-y-3 text-sm text-white/72">
                                <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
                                    <p className="font-semibold text-white">Status de tracking</p>
                                    <p className="mt-1">{trackingStatusLabel[trackingState]}</p>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
                                    <p className="font-semibold text-white">Status do modelo</p>
                                    <p className="mt-1">{modelStatusLabel[modelState]}</p>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
                                    <p className="font-semibold text-white">Feed da camera</p>
                                    <p className="mt-1">{cameraFeedVisible ? 'Video ativo' : 'Aguardando video'}</p>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
                                    <p className="font-semibold text-white">Alvo atual</p>
                                    <p className="mt-1">{experience?.targetName || 'Carregando...'}</p>
                                </div>
                            </div>

                            <div className="mt-5 flex flex-wrap gap-3">
                                {!started && !autoStart ? (
                                    <button type="button" onClick={startAr} disabled={!experience || !!loadError} className="rounded-full bg-primary px-5 py-3 text-sm font-black text-black transition hover:brightness-105 disabled:opacity-50">
                                        Ver AR
                                    </button>
                                ) : !started && autoStart ? (
                                    <div className="rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white/75">
                                        Preparando camera...
                                    </div>
                                ) : (
                                    <button type="button" onClick={stopArSession} className="rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10">
                                        Encerrar sessao
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
                            <p className="text-xs uppercase tracking-[0.24em] text-white/45">Instrucoes</p>
                            <div className="mt-4 space-y-3 text-sm text-white/72">
                                <p>1. Escaneie o QR Code da loja para abrir esta pagina.</p>
                                <p>2. Toque em <span className="font-semibold text-white">Ver AR</span> para liberar a camera.</p>
                                {instructions.map((instruction) => (
                                    <p key={instruction}>{instruction}</p>
                                ))}
                            </div>
                            {experience?.previewImageUrl && activeMode === 'image-tracking' && (
                                <div className="mt-5 overflow-hidden rounded-2xl border border-white/10 bg-black/30">
                                    <img src={experience.previewImageUrl} alt="Imagem-alvo de exemplo" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                                </div>
                            )}
                            {activeMode === 'marker-tracking' && (
                                <div className="mt-5 rounded-2xl border border-dashed border-white/15 bg-black/30 px-4 py-4 text-sm text-white/65">
                                    Use o marcador configurado no catalogo central da feature AR. Para customizar, defina `markerPreset` ou `markerPatternUrl`.
                                </div>
                            )}
                        </div>

                        {(loadError || bootError || isErrorState) && (
                            <div className="rounded-[2rem] border border-amber-500/20 bg-amber-500/10 p-5 text-sm text-amber-100">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                    <div className="space-y-2">
                                        <p className="font-semibold">Fallback amigavel</p>
                                        <p>{loadError || bootError || 'Nao foi possivel concluir a sessao AR.'}</p>
                                        <p>Verifique permissao de camera, HTTPS no celular e se o modelo/target configurados estao acessiveis.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {debugEnabled && debugSnapshot && (
                            <div className="rounded-[2rem] border border-cyan-500/20 bg-cyan-500/10 p-5 text-xs text-cyan-50">
                                <p className="font-semibold uppercase tracking-[0.24em] text-cyan-200">Debug</p>
                                <div className="mt-3 space-y-2">
                                    <p>tracking mode: {debugSnapshot.trackingMode}</p>
                                    <p>tracking state: {debugSnapshot.trackingState}</p>
                                    <p>target visible: {debugSnapshot.targetVisible ? 'yes' : 'no'}</p>
                                    <p>model state: {debugSnapshot.modelState}</p>
                                    <p>produto: {debugSnapshot.itemName}</p>
                                    <p>config key: {debugSnapshot.configKey}</p>
                                    <p>target: {debugSnapshot.targetName}</p>
                                    <p>model url: {debugSnapshot.modelUrl}</p>
                                    <p>transform: scale {debugSnapshot.transform.scale}</p>
                                    <p>position: {debugSnapshot.transform.position}</p>
                                    <p>rotation: {debugSnapshot.transform.rotation}</p>
                                </div>
                            </div>
                        )}

                        <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
                            <div className="flex items-center gap-3">
                                <QrCode className="h-5 w-5 text-primary" />
                                <div>
                                    <p className="text-sm font-bold text-white">Fluxo via QR</p>
                                    <p className="text-xs text-white/55">Use o QR desta rota para abrir direto nesta experiencia em celular real.</p>
                                </div>
                            </div>
                            <p className="mt-4 break-all rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-xs text-white/70">
                                {window.location.href}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StoreArPage;
